/**
 * axiosAuthInterceptor — sends the session cookie on every API call.
 *
 * Replaces the Bearer-token interceptor from the shared-password era. The
 * session now lives in an httpOnly cookie, so the only thing we have to do is
 * ask axios to include credentials on cross-site requests (the SPA and the API
 * are on different hosts in preview and in production).
 *
 * A 401 means the server threw our session away — the AuthProvider listens for
 * the event and drops the user, which routes back to the login screen.
 */
import axios from "axios";
import { SESSION_EXPIRED_EVENT } from "@/contexts/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

let installed = false;

function isApiCall(url) {
  const u = url || "";
  return u.startsWith(`${API_BASE}/api/`) || u.startsWith("/api/");
}

export function installAuthInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    if (isApiCall(config.url)) config.withCredentials = true;
    return config;
  });

  axios.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      // /auth/me legitimately 401s while checking whether anyone is signed
      // in, and the admin password form surfaces its own 401.
      const isAuthProbe = /\/api\/(auth\/me|admin\/auth)\b/.test(url);
      if (status === 401 && isApiCall(url) && !isAuthProbe) {
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
      return Promise.reject(err);
    },
  );
}
